// =============================================================================
// BookingScheduleTableV2.tsx — Bookings V2 grid (court × time).
//
// Same spanning engine as the V1 BookingScheduleTable (rowSpan + skipRender,
// first-booking-wins claiming, swipeable mobile view) — that logic is left
// intact. What changed:
//   - renders BookingCellV2 instead of BookingCell
//   - opens MakeBookingV2 (the restyled modal) on cell click; the stray
//     AvailableSlotCard tag is dropped (the modal opens directly)
//   - Fortius grid chrome: radius-12 white container with a 4px teal top accent
//     bar, tinted header row, hairline dividers — driven by bookingTokens.ts
//     (the FORT-178 seam), matching Member Profile V2
// =============================================================================

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSwipeable } from 'react-swipeable';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { BookingCellV2 } from './BookingCellV2';
import { Booking } from '../../../model/bookings/Booking';
import { Slot } from '../../../model/bookings/Slot';
import { FacilityScheduleForDay } from '../../../model/bookings/FacilityScheduleForDay';
import MakeBookingV2 from './MakeBookingV2';
import { useBookingFactory } from '../useBookingFactory';
import { Facility } from '../../../model/facilities/Facility';
import { PlayingMember } from '../../../model/members/PlayingMember';
import { formatInTimezone } from '../../../utils/TimezoneUtils';
import { BookingStatus } from '../../../model/bookings/BookingStatus';
import { useClientConfig } from '../../../common/client/ClientConfigProvider';
import { bookingColors, bookingRadii, slotPalette } from './bookingTokens';

interface SelectedBookingData {
  booking: Booking | null;
  facilityName: string;
  slot: Slot;
}

interface BookingScheduleTableV2Props {
  schedules: FacilityScheduleForDay[];
  onBookingSuccess: () => void;
  selectedDate: Date;
  initialFacilityIndex?: number;
  onFacilityChange?: (index: number) => void;
  members: PlayingMember[];
}

interface GridCell {
  slot: Slot;
  booking: Booking | null;
  rowSpan: number;
  skipRender: boolean;
}

type FacilityGridMap = Map<string, Map<number, GridCell>>;

const toDate = (value: Date | string): Date => {
  return value instanceof Date ? value : new Date(value);
};

// Must match ROW_HEIGHT_PX in BookingCellV2 so booked and empty rows are the
// same (compact) height per interval.
const ROW_HEIGHT = '48px';

// Column marker for a facility taken out of service (FORT-401). A muted pill
// rather than a status colour: "unavailable" is a configuration state, not a
// booking status, and must not read as pending/overdue in the status trio.
const UnavailableTag: React.FC = () => (
  <Box
    component="span"
    data-testid="facility-unavailable-tag"
    sx={{
      display: 'inline-block',
      ml: '6px',
      px: '6px',
      py: '1px',
      borderRadius: bookingRadii.tag,
      border: `1px solid ${bookingColors.line}`,
      background: slotPalette.blocked.base,
      color: bookingColors.inkMuted,
      fontSize: '9px',
      fontWeight: 700,
      letterSpacing: '0.4px',
      textTransform: 'uppercase',
      verticalAlign: 'middle',
      whiteSpace: 'nowrap',
    }}
  >
    Unavailable
  </Box>
);

export const BookingScheduleTableV2: React.FC<BookingScheduleTableV2Props> = ({
  schedules,
  onBookingSuccess,
  initialFacilityIndex = 0,
  onFacilityChange,
  members,
}) => {
  const { locale } = useClientConfig();
  const [currentFacilityIndex, setCurrentFacilityIndex] = useState(initialFacilityIndex);
  const [selectedBookingData, setSelectedBookingData] = useState<SelectedBookingData | null>(null);
  const [swipeAnimation, setSwipeAnimation] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [selectedFacility, setSelectedFacility] = useState<Facility | null>(null);
  const [isFacilityLoading, setIsFacilityLoading] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    facility: currentFacility,
    isLoading: isCurrentFacilityLoading,
    bookingService,
  } = useBookingFactory(schedules[currentFacilityIndex]?.facilityName || '');

  useEffect(() => {
    setCurrentFacilityIndex(initialFacilityIndex);
  }, [initialFacilityIndex]);

  useEffect(() => {
    const fetchSelectedFacility = async () => {
      if (selectedBookingData && bookingService) {
        setIsFacilityLoading(true);
        try {
          const facilityData = await bookingService.getFacility(selectedBookingData.facilityName);
          setSelectedFacility(facilityData);
        } catch (err) {
          console.error('Error fetching selected facility:', err);
        } finally {
          setIsFacilityLoading(false);
        }
      } else {
        setSelectedFacility(null);
      }
    };
    fetchSelectedFacility();
  }, [selectedBookingData, bookingService]);

  const { facilityNames, sortedTimeSlots, gridMap, timezoneForDisplay, inactiveFacilities } = useMemo(() => {
    const facilityNames = schedules.map((s) => s.facilityName);
    // Facilities taken out of service (FORT-401). Hidden ones never reach the grid
    // at all — the server filters them — so this only ever covers the
    // inactive-but-visible case the design wants greyed rather than dropped.
    const inactiveFacilities = new Set(
      schedules.filter((s) => s.isActive === false).map((s) => s.facilityName)
    );
    const timezoneForDisplay = schedules[0]?.timezone || locale.timezone;

    const allTimesSet = new Set<number>();
    schedules.forEach((schedule) => {
      schedule.slots.forEach((slot) => {
        allTimesSet.add(toDate(slot.startTime).getTime());
      });
    });
    const sortedTimeSlots = Array.from(allTimesSet).sort((a, b) => a - b);

    const gridMap: FacilityGridMap = new Map();

    schedules.forEach((schedule) => {
      const facilityMap = new Map<number, GridCell>();
      const { facilityName, slots, bookings, bookingIntervalMins } = schedule;

      const sortedBookings = [...bookings]
        .filter((b) => b.bookingStatus !== BookingStatus.Cancelled.toString())
        .sort(
          (a, b) => toDate(a.bookingDateTimeUtc).getTime() - toDate(b.bookingDateTimeUtc).getTime()
        );

      const claimedSlots = new Set<number>();
      const bookingStartSlots = new Map<number, { booking: Booking; rowSpan: number }>();

      sortedBookings.forEach((booking) => {
        const startMs = toDate(booking.bookingDateTimeUtc).getTime();
        const endMs = toDate(booking.bookingEndTimeUtc).getTime();

        if (claimedSlots.has(startMs)) {
          return;
        }

        const bookingSlots: number[] = [];
        let currentMs = startMs;
        while (currentMs < endMs) {
          if (!claimedSlots.has(currentMs)) {
            bookingSlots.push(currentMs);
          } else {
            break;
          }
          currentMs += bookingIntervalMins * 60 * 1000;
        }

        bookingSlots.forEach((slotMs) => claimedSlots.add(slotMs));

        if (bookingSlots.length > 0) {
          bookingStartSlots.set(startMs, {
            booking,
            rowSpan: bookingSlots.length,
          });
        }
      });

      slots.forEach((slot) => {
        const timeMs = toDate(slot.startTime).getTime();
        const bookingStart = bookingStartSlots.get(timeMs);

        if (bookingStart) {
          facilityMap.set(timeMs, {
            slot,
            booking: bookingStart.booking,
            rowSpan: bookingStart.rowSpan,
            skipRender: false,
          });
        } else if (claimedSlots.has(timeMs)) {
          facilityMap.set(timeMs, {
            slot,
            booking: null,
            rowSpan: 1,
            skipRender: true,
          });
        } else {
          facilityMap.set(timeMs, {
            slot,
            booking: null,
            rowSpan: 1,
            skipRender: false,
          });
        }
      });

      gridMap.set(facilityName, facilityMap);
    });

    return { facilityNames, sortedTimeSlots, gridMap, timezoneForDisplay, inactiveFacilities };
  }, [schedules, locale.timezone]);

  const isFacilityActive = (facilityName: string) => !inactiveFacilities.has(facilityName);

  const getCell = (facilityName: string, timeMs: number): GridCell | null => {
    return gridMap.get(facilityName)?.get(timeMs) || null;
  };

  const handleCellClick = (booking: Booking | null, slot: Slot, facilityName: string) => {
    setSelectedBookingData({
      booking: booking || null,
      facilityName,
      slot,
    });
  };

  const handleCloseModals = (wasSuccessful: boolean = false) => {
    setSelectedBookingData(null);
    setSelectedFacility(null);
    if (wasSuccessful) {
      onBookingSuccess();
    }
  };

  const handleFacilityChange = (newIndex: number, direction: 'left' | 'right') => {
    setIsAnimating(true);
    const startPosition = direction === 'left' ? 100 : -100;
    setSwipeAnimation(startPosition);

    if (contentRef.current) {
      void contentRef.current.offsetHeight;
    }

    requestAnimationFrame(() => {
      setSwipeAnimation(0);
      setTimeout(() => {
        setCurrentFacilityIndex(newIndex);
        onFacilityChange?.(newIndex);
        setIsAnimating(false);
      }, 300);
    });
  };

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (currentFacilityIndex < schedules.length - 1 && !isAnimating) {
        handleFacilityChange(currentFacilityIndex + 1, 'left');
      }
    },
    onSwipedRight: () => {
      if (currentFacilityIndex > 0 && !isAnimating) {
        handleFacilityChange(currentFacilityIndex - 1, 'right');
      }
    },
    onSwiping: (e) => {
      if (
        (currentFacilityIndex === 0 && e.deltaX > 0) ||
        (currentFacilityIndex === schedules.length - 1 && e.deltaX < 0)
      ) {
        return;
      }
      setSwipeAnimation(e.deltaX);
    },
    onSwiped: () => {
      setSwipeAnimation(0);
    },
    trackMouse: true,
    preventScrollOnSwipe: true,
    delta: 10,
    swipeDuration: 500,
    touchEventOptions: { passive: false },
  });

  const formatTimeSlot = (timeMs: number): string => {
    return formatInTimezone(new Date(timeMs), timezoneForDisplay, 'HH:mm');
  };

  // Shared header-cell styling for the desktop grid (tinted, hairline dividers).
  const headCellSx = {
    background: bookingColors.surfaceTint,
    color: bookingColors.inkMuted,
    fontWeight: 700,
    fontSize: '12px',
    padding: '10px 8px',
    borderBottom: `1px solid ${bookingColors.line}`,
  } as const;

  const DesktopView = () => {
    const timeColumnWidth = '88px';
    const facilityColumnWidth = `calc((100% - ${timeColumnWidth}) / ${facilityNames.length})`;

    return (
      <TableContainer
        sx={{
          border: `1px solid ${bookingColors.line}`,
          borderTop: `4px solid ${bookingColors.teal}`,
          borderRadius: '12px',
          overflow: 'hidden',
          background: bookingColors.bgCard,
          boxShadow: '0 1px 2px rgba(31,42,46,0.05)',
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed', borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...headCellSx,
                  width: timeColumnWidth,
                  height: ROW_HEIGHT,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '1.2px',
                  color: bookingColors.inkFaint,
                }}
              >
                Time
              </TableCell>
              {facilityNames.map((name) => {
                const active = isFacilityActive(name);
                return (
                  <TableCell
                    key={name}
                    data-testid={`booking-facility-header-${name.replace(/\s+/g, '-').toLowerCase()}`}
                    data-facility-active={active ? 'true' : 'false'}
                    sx={{
                      ...headCellSx,
                      width: facilityColumnWidth,
                      height: ROW_HEIGHT,
                      textAlign: 'center',
                      borderLeft: `1px solid ${bookingColors.line}`,
                      ...(active ? {} : { color: bookingColors.inkFaint }),
                    }}
                  >
                    {name}
                    {!active && <UnavailableTag />}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedTimeSlots.map((timeMs) => (
              <TableRow key={timeMs}>
                <TableCell
                  sx={{
                    height: ROW_HEIGHT,
                    padding: '3px 8px',
                    fontSize: '13px',
                    color: bookingColors.inkMuted,
                    textAlign: 'center',
                    background: bookingColors.surfaceTint,
                    borderRight: `1px solid ${bookingColors.line}`,
                    borderTop: `1px solid ${bookingColors.line}`,
                  }}
                >
                  {formatTimeSlot(timeMs)}
                </TableCell>
                {facilityNames.map((facilityName) => {
                  const cell = getCell(facilityName, timeMs);
                  const schedule = schedules.find((s) => s.facilityName === facilityName);

                  if (!cell) {
                    return (
                      <TableCell
                        key={`${facilityName}-${timeMs}`}
                        sx={{
                          width: facilityColumnWidth,
                          height: ROW_HEIGHT,
                          padding: 0,
                          borderLeft: `1px solid ${bookingColors.line}`,
                          borderTop: `1px solid ${bookingColors.line}`,
                        }}
                      />
                    );
                  }

                  if (cell.skipRender) {
                    return null;
                  }

                  return (
                    <TableCell
                      key={`${facilityName}-${timeMs}`}
                      sx={{
                        width: facilityColumnWidth,
                        height: ROW_HEIGHT,
                        verticalAlign: 'top',
                        padding: '3px 4px',
                        borderLeft: `1px solid ${bookingColors.line}`,
                        borderTop: `1px solid ${bookingColors.line}`,
                      }}
                      rowSpan={cell.rowSpan}
                    >
                      <BookingCellV2
                        slot={cell.slot}
                        booking={cell.booking}
                        bookingIntervalMins={schedule?.bookingIntervalMins || 30}
                        facilityName={facilityName}
                        onCellClick={handleCellClick}
                        facilityIsActive={isFacilityActive(facilityName)}
                        fillHeight
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const MobileView = () => {
    const currentSchedule = schedules[currentFacilityIndex];
    const currentFacilityName = currentSchedule?.facilityName;

    const facilityTimeSlots = sortedTimeSlots.filter((timeMs) => {
      const cell = getCell(currentFacilityName, timeMs);
      return cell && !cell.skipRender;
    });

    return (
      <div
        className="mobile-schedule-container"
        style={{
          width: '100%',
          backgroundColor: bookingColors.bgCard,
          border: `1px solid ${bookingColors.line}`,
          borderTop: `4px solid ${bookingColors.teal}`,
          borderRadius: '12px',
          boxShadow: '0 1px 2px rgba(31,42,46,0.05)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px',
            background: bookingColors.surfaceTint,
            borderBottom: `1px solid ${bookingColors.line}`,
          }}
        >
          <IconButton
            onClick={() =>
              !isAnimating &&
              currentFacilityIndex > 0 &&
              handleFacilityChange(currentFacilityIndex - 1, 'right')
            }
            disabled={currentFacilityIndex === 0 || isAnimating}
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="h6"
            data-testid={`booking-facility-header-${(currentFacilityName ?? '').replace(/\s+/g, '-').toLowerCase()}`}
            data-facility-active={isFacilityActive(currentFacilityName) ? 'true' : 'false'}
            sx={{
              fontSize: '16px',
              fontWeight: 600,
              color: isFacilityActive(currentFacilityName) ? bookingColors.ink : bookingColors.inkFaint,
            }}
          >
            {currentFacilityName}
            {!isFacilityActive(currentFacilityName) && <UnavailableTag />}
          </Typography>
          <IconButton
            onClick={() =>
              !isAnimating &&
              currentFacilityIndex < schedules.length - 1 &&
              handleFacilityChange(currentFacilityIndex + 1, 'left')
            }
            disabled={currentFacilityIndex === schedules.length - 1 || isAnimating}
          >
            <ChevronRight />
          </IconButton>
        </div>

        <div
          {...handlers}
          style={{
            // Clip only horizontally so the swipe (translateX) is masked, while
            // the schedule flows to its full natural height. A full `overflow:
            // hidden` + flex:1 + height:100% chain collapsed the pane and cut off
            // everything below a tall booking.
            overflowX: 'hidden',
            position: 'relative',
            width: '100%',
          }}
        >
          <div
            ref={contentRef}
            style={{
              transform: `translateX(${swipeAnimation}px)`,
              transition: swipeAnimation === 0 ? 'transform 0.3s ease-out' : 'none',
              width: '100%',
            }}
          >
            <Table
              size="small"
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                borderCollapse: 'collapse',
                '& td': { boxSizing: 'border-box' },
              }}
            >
              <TableBody>
                {facilityTimeSlots.map((timeMs) => {
                  const cell = getCell(currentFacilityName, timeMs);
                  if (!cell) return null;

                  return (
                    <TableRow key={timeMs}>
                      {/* Mobile filters out skipRender (covered) slots, so each
                          visible slot is its own row — do NOT use the interval
                          rowSpan here (it would swallow the next real rows). The
                          booking cell's own height (span * ROW_HEIGHT) makes a
                          multi-slot booking tall; the time cell matches via the
                          row height. */}
                      <TableCell
                        sx={{
                          width: '28%',
                          padding: '3px 8px',
                          verticalAlign: 'top',
                          whiteSpace: 'nowrap',
                          fontSize: '11px',
                          color: bookingColors.inkMuted,
                          background: bookingColors.surfaceTint,
                          borderRight: `1px solid ${bookingColors.line}`,
                          borderTop: `1px solid ${bookingColors.line}`,
                        }}
                      >
                        {formatTimeSlot(timeMs)}
                      </TableCell>
                      <TableCell
                        sx={{
                          width: '72%',
                          padding: '3px 4px',
                          borderTop: `1px solid ${bookingColors.line}`,
                        }}
                      >
                        <BookingCellV2
                          slot={cell.slot}
                          booking={cell.booking}
                          bookingIntervalMins={currentSchedule.bookingIntervalMins}
                          facilityName={currentFacilityName}
                          onCellClick={handleCellClick}
                          facilityIsActive={isFacilityActive(currentFacilityName)}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  };

  const facilityToUse = isMobile ? currentFacility : selectedFacility;
  const isLoadingFacility = isMobile ? isCurrentFacilityLoading : isFacilityLoading;

  return (
    <>
      {isMobile ? <MobileView /> : <DesktopView />}

      {selectedBookingData && !isLoadingFacility && facilityToUse && (
        <MakeBookingV2
          facility={facilityToUse}
          booking={selectedBookingData.booking}
          onClose={handleCloseModals}
          slot={selectedBookingData.slot}
          members={members}
        />
      )}
    </>
  );
};

export default BookingScheduleTableV2;
